// SPDX-License-Identifier: AGPL-3.0-only
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Modal,
  NumberInput,
  PasswordInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';

import { ApiError, api } from '../api/client';
import type { Company, Site } from '../api/client';
import type { Device, Driver } from '../api/devices';
import { devicesApi } from '../api/devices';

/**
 * The devices a tenant manages.
 *
 * # ADDING A DEVICE IS WHERE THE CONNECTION DETAILS COME FROM
 *
 * The operator picks a driver, says where the switch is, and types the account
 * and password once. That is the whole of it: the driver describes how the model
 * is managed, and this describes which physical thing is being managed.
 *
 * The password goes straight to the server, into the credential store, enveloped.
 * No route returns it and no view here has a field for it — `credential_set` is
 * a boolean, because "has somebody entered a password?" is a question worth
 * answering and "which password" is not.
 *
 * # NOTHING IS CONTACTED BY ADDING IT
 *
 * A new device is untrusted. Its first contact is a separate, deliberate act on
 * its own page, where the fingerprint can be looked at by a person.
 */
export function Devices() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [adding, setAdding] = useState(false);

  async function refresh() {
    try {
      setDevices(await devicesApi.list());
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err : null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  if (loading) {
    return (
      <Center h={220}>
        <Loader />
      </Center>
    );
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>Devices</Title>
        <Button onClick={() => setAdding(true)}>Add device</Button>
      </Group>

      {error ? (
        <Alert color="red" title="Could not load devices">
          {error.problem.detail ?? error.message}
        </Alert>
      ) : null}

      {devices.length === 0 ? (
        <Card withBorder radius="md" p="xl">
          <Stack align="center" gap="xs">
            <Text fw={600}>No devices yet</Text>
            <Text size="sm" c="dimmed" ta="center" maw={460}>
              Add a switch by choosing the driver for its model, telling Meshvara where it is, and
              giving it an account to manage it with. Nothing is contacted until you have seen and
              confirmed the device’s SSH identity.
            </Text>
            <Button mt="sm" onClick={() => setAdding(true)}>
              Add device
            </Button>
          </Stack>
        </Card>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          {devices.map((device) => (
            <Card
              key={device.id}
              withBorder
              radius="md"
              padding="md"
              component={Link}
              to={`/devices/${device.id}`}
              style={{ textDecoration: 'none' }}
            >
              <Stack gap={6}>
                <Group justify="space-between" wrap="nowrap">
                  <Text fw={600}>{device.name}</Text>
                  <TrustBadge state={device.ssh_trust_state} />
                </Group>
                <Text size="sm" c="dimmed">
                  {device.driver_id}
                </Text>
                <Text size="xs" c="dimmed" ff="monospace">
                  {device.management_address}:{device.management_port}
                </Text>
                {!device.credential_set ? (
                  <Badge color="orange" variant="light" size="sm">
                    No credential
                  </Badge>
                ) : null}
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      )}

      <AddDevice
        opened={adding}
        onClose={() => setAdding(false)}
        onAdded={async () => {
          setAdding(false);
          await refresh();
        }}
      />
    </Stack>
  );
}

/**
 * The trust state, at a glance.
 *
 * `changed` is red and says so in words. A badge that only differed by colour
 * would be the same badge to a colourblind operator, and this is the one state
 * that can mean somebody is on the wire.
 */
function TrustBadge({ state }: { state: Device['ssh_trust_state'] }) {
  switch (state) {
    case 'trusted':
      return (
        <Badge color="teal" variant="light" size="sm">
          Trusted
        </Badge>
      );
    case 'pending':
      return (
        <Badge color="blue" variant="light" size="sm">
          Needs review
        </Badge>
      );
    case 'changed':
      return (
        <Badge color="red" size="sm">
          Identity changed
        </Badge>
      );
    default:
      return (
        <Badge color="gray" variant="light" size="sm">
          Not contacted
        </Badge>
      );
  }
}

function AddDevice({
  opened,
  onClose,
  onAdded,
}: {
  opened: boolean;
  onClose: () => void;
  onAdded: () => Promise<void>;
}) {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [gateways, setGateways] = useState<{ value: string; label: string }[]>([]);

  const [driverId, setDriverId] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [siteId, setSiteId] = useState<string | null>(null);
  const [gatewayId, setGatewayId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [port, setPort] = useState<number>(22);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!opened) return;
    void (async () => {
      try {
        const [d, c] = await Promise.all([devicesApi.drivers(), api.companies()]);
        setDrivers(d);
        setCompanies(c.items);
      } catch (err) {
        setError(err instanceof ApiError ? err : null);
      }
    })();
  }, [opened]);

  useEffect(() => {
    if (!companyId) {
      setSites([]);
      return;
    }
    void (async () => {
      try {
        setSites((await api.sites(companyId)).items);
        // ONLY ACTIVE GATEWAYS. A revoked one cannot carry a job, and offering
        // it would produce a device that can never be read for a reason the
        // form already knew.
        setGateways(
          (await api.gateways(companyId)).items
            .filter((g) => g.state === 'active')
            .map((g) => ({ value: g.id, label: g.name })),
        );
      } catch (err) {
        setError(err instanceof ApiError ? err : null);
      }
    })();
  }, [companyId]);

  // The driver's default port is a starting value, not a constraint: a switch on
  // a non-standard port is ordinary, and the field stays editable.
  useEffect(() => {
    const driver = drivers.find((d) => d.driver_id === driverId);
    if (driver) setPort(driver.default_port);
  }, [driverId, drivers]);

  const fieldErrors = error?.fieldErrors ?? {};

  return (
    <Modal opened={opened} onClose={busy ? () => undefined : onClose} title="Add device" centered>
      <Stack>
        {error && Object.keys(fieldErrors).length === 0 ? (
          <Alert color="red">{error.problem.detail ?? error.message}</Alert>
        ) : null}

        <Select
          label="Driver"
          description="The model this switch is. Meshvara manages it the way this driver describes."
          data={drivers.map((d) => ({
            value: d.driver_id,
            label: `${d.name} (${d.supported_models.join(', ')})`,
          }))}
          value={driverId}
          onChange={setDriverId}
          error={fieldErrors.driver_id}
          searchable
        />

        <Select
          label="Company"
          data={companies.map((c) => ({ value: c.id, label: c.name }))}
          value={companyId}
          onChange={(v) => {
            setCompanyId(v);
            setSiteId(null);
            setGatewayId(null);
          }}
          error={fieldErrors.company_id}
        />

        <Select
          label="Site"
          data={sites.map((s) => ({ value: s.id, label: s.name }))}
          value={siteId}
          onChange={setSiteId}
          disabled={!companyId}
          error={fieldErrors.site_id}
        />

        <Select
          label="Gateway"
          description="The Gateway that can reach this device on the customer’s network."
          data={gateways}
          value={gatewayId}
          onChange={setGatewayId}
          disabled={!companyId}
          error={fieldErrors.gateway_id}
        />

        <TextInput
          label="Name"
          placeholder="Main office switch"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          error={fieldErrors.name}
        />

        <Group grow align="flex-start">
          <TextInput
            label="Management address"
            placeholder="192.168.1.20"
            value={address}
            onChange={(e) => setAddress(e.currentTarget.value)}
            error={fieldErrors.management_address}
          />
          <NumberInput
            label="Port"
            value={port}
            onChange={(v) => setPort(typeof v === 'number' ? v : 22)}
            min={1}
            max={65535}
            error={fieldErrors.management_port}
          />
        </Group>

        <TextInput
          label="Username"
          value={username}
          onChange={(e) => setUsername(e.currentTarget.value)}
          error={fieldErrors.management_username}
        />

        <PasswordInput
          label="Password"
          description="Stored encrypted. Meshvara never shows it again and no API returns it."
          value={password}
          onChange={(e) => setPassword(e.currentTarget.value)}
          error={fieldErrors.management_password}
        />

        <Alert color="blue" variant="light">
          Adding a device does not contact it. You will be asked to check and confirm its SSH
          identity before Meshvara sends anything.
        </Alert>

        <Group justify="flex-end">
          <Button variant="default" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await devicesApi.create({
                  driver_id: driverId ?? '',
                  company_id: companyId ?? '',
                  site_id: siteId ?? '',
                  gateway_id: gatewayId ?? '',
                  name,
                  management_address: address,
                  management_port: port,
                  management_username: username,
                  management_password: password,
                });
                setPassword('');
                await onAdded();
              } catch (err) {
                setError(err instanceof ApiError ? err : null);
              } finally {
                setBusy(false);
              }
            }}
          >
            Add device
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
